import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { guestGuard } from './core/guards/guest.guard';
import { otpGuard } from './core/guards/otp.guard';
import { roleGuard } from './core/guards/role.guard';

export const routes: Routes = [
  // ── 1. Dynamic Entry Links (Partner Invites) ──────────────────────────────────
  {
    path: 'join/:subTenant',
    canActivate: [guestGuard],
    loadComponent: () => import('./pages/auth/join/join').then((m) => m.Join),
  },
  {
    path: 'join/:tenant/:subTenant',
    canActivate: [guestGuard],
    loadComponent: () => import('./pages/auth/join/join').then((m) => m.Join),
  },

  // ── 2. Public / Authentication Routes ─────────────────────────────────────────
  {
    path: 'auth/login',
    canActivate: [guestGuard],
    loadComponent: () => import('./pages/auth/login/login').then((m) => m.Login),
  },
  {
    path: 'auth/signup',
    canActivate: [guestGuard],
    loadComponent: () => import('./pages/auth/signup/signup').then((m) => m.Signup),
  },
  {
    path: 'auth/verify-otp',
    canActivate: [otpGuard],
    loadComponent: () => import('./pages/auth/verify-otp/verify-otp').then((m) => m.VerifyOtp),
  },
  {
    path: 'auth/forgot-password',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./pages/auth/forgot-password/forgot-password').then((m) => m.ForgotPassword),
  },
  {
    path: 'auth/forgot-password/:userId',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./pages/auth/reset-password/reset-password').then((m) => m.ResetPassword),
  },
  {
    path: 'auth/reset-password',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./pages/auth/reset-password/reset-password').then((m) => m.ResetPassword),
  },
  {
    path: 'auth/reset-password/:userId',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./pages/auth/reset-password/reset-password').then((m) => m.ResetPassword),
  },
  {
    path: 'reset-password',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./pages/auth/reset-password/reset-password').then((m) => m.ResetPassword),
  },
  {
    path: 'reset-password/:userId',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./pages/auth/reset-password/reset-password').then((m) => m.ResetPassword),
  },
  {
    path: 'forgot-password/:userId',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./pages/auth/reset-password/reset-password').then((m) => m.ResetPassword),
  },
  {
    path: 'forgot-password',
    redirectTo: 'auth/forgot-password',
    pathMatch: 'full',
  },

  // ── 3. Applicant Portal ───────────────────────────────────────────────────────
  {
    path: 'card-request/received',
    canActivate: [authGuard, roleGuard(['applicant', 'user'])],
    loadComponent: () =>
      import(
        './pages/card-request/pages/request-received/request-received.component'
      ).then((m) => m.RequestReceivedComponent),
  },
  {
    path: 'card-request/status',
    canActivate: [authGuard, roleGuard(['applicant', 'user'])],
    loadComponent: () =>
      import(
        './pages/card-request/pages/card-status/card-status.component'
      ).then((m) => m.CardStatusComponent),
  },
  {
    path: 'card-request/profile',
    canActivate: [authGuard, roleGuard(['applicant', 'user'])],
    loadComponent: () =>
      import(
        './pages/card-request/pages/profile/profile.component'
      ).then((m) => m.ProfileComponent),
  },
  {
    path: 'profile',
    redirectTo: 'card-request/profile',
    pathMatch: 'full',
  },
  {
    path: 'card-request',
    canActivate: [authGuard, roleGuard(['applicant', 'user'])],
    loadComponent: () => import('./pages/card-request/card-request').then((m) => m.CardRequest),
    children: [
      {
        path: '',
        redirectTo: 'identity',
        pathMatch: 'full',
      },
      {
        path: 'identity',
        loadComponent: () =>
          import('./pages/card-request/steps/step1-identity/step1-identity.component').then(
            (m) => m.Step1IdentityComponent
          ),
      },
      {
        path: 'contact-address',
        loadComponent: () =>
          import(
            './pages/card-request/steps/step2-contact-address/step2-contact-address.component'
          ).then((m) => m.Step2ContactAddressComponent),
      },
      {
        path: 'professional',
        loadComponent: () =>
          import(
            './pages/card-request/steps/step3-professional/step3-professional.component'
          ).then((m) => m.Step3ProfessionalComponent),
      },
      {
        path: 'card-document',
        loadComponent: () =>
          import(
            './pages/card-request/steps/step4-card-document/step4-card-document.component'
          ).then((m) => m.Step4CardDocumentComponent),
      },
      {
        path: 'review-submit',
        loadComponent: () =>
          import(
            './pages/card-request/steps/step5-review-submit/step5-review-submit.component'
          ).then((m) => m.Step5ReviewSubmitComponent),
      },
    ],
  },
  // Alias for /applicant route
  {
    path: 'applicant',
    redirectTo: 'card-request',
    pathMatch: 'prefix',
  },

  // ── 4. Approver Portal ────────────────────────────────────────────────────────
  {
    path: 'approver',
    canActivate: [authGuard, roleGuard(['approver'])],
    loadComponent: () =>
      import('./layout/approver-layout/approver-layout.component').then(
        (m) => m.ApproverLayoutComponent
      ),
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./pages/approver/dashboard/dashboard.component').then(
            (m) => m.ApproverDashboardComponent
          ),
      },
    ],
  },

  // ── 5. Tenant Admin Portal ───────────────────────────────────────────────────
  {
    path: 'admin',
    canActivate: [authGuard, roleGuard(['tenantadmin', 'admin'])],
    loadComponent: () =>
      import('./layout/admin-layout/admin-layout').then((m) => m.AdminLayout),
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./pages/admin/dashboard/dashboard').then((m) => m.Dashboard),
      },
      {
        path: 'approver',
        loadComponent: () =>
          import('./pages/admin/approvers/approvers').then((m) => m.Approvers),
      },
      {
        path: 'approvers',
        redirectTo: 'approver',
        pathMatch: 'full',
      },
      {
        path: 'sub-tenants',
        loadComponent: () =>
          import('./pages/admin/sub-tenants/sub-tenants').then((m) => m.SubTenants),
      },
    ],
  },

  // ── 6. Root & Global Fallbacks ────────────────────────────────────────────────
  {
    path: '',
    redirectTo: 'auth/login',
    pathMatch: 'full',
  },
  {
    path: '**',
    redirectTo: 'auth/login',
  },
];
